/**
 * src/components/SearchableSelect.tsx — Generic searchable select input.
 *
 * v0.4.9.2.3c22a: replaces the free-text `<Input>` for country across all
 * three admin pages (Most Wanted, Missing Persons, Stolen Vehicles) and
 * the city field on Missing Persons. The user types a substring; the
 * component filters options locally; the form stores the option's `value`
 * (alpha-3 country code or ISO-3166-2 subdivision code) — never the
 * display label.
 *
 * Behavior:
 *   - Empty value shows a placeholder hint ("Search country…").
 *   - Typing filters case-insensitive against `option.label` AND `option.code`.
 *   - Click on an option (or Enter on highlighted one) calls `onChange(code)` and
 *     closes the menu.
 *   - Click-outside closes the menu without changing the value.
 *   - Backspace / clear button (when a value is selected) returns to empty state.
 *   - Disabled while `loading` is true (used to gate the dynamic import for
 *     subdivisions so users see "Loading…" while the chunk fetches).
 *
 * Accessibility: keyboard support (↑/↓ to navigate, Enter to select,
 * Escape to close, Backspace to clear).
 */
import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Loader2, X } from 'lucide-react';

export interface SearchableOption {
  /** Stored value (alpha-3 country code, ISO-3166-2 subdivision code, etc.) */
  value: string;
  /** Display label shown in the trigger and in the menu */
  label: string;
  /** Optional secondary text shown beneath the label (e.g. "ESP" beneath "Spain") */
  hint?: string;
}

interface Props {
  /**
   * v0.4.9.2.4c2: optional. Pass `""` (or omit) when the host page
   * already renders its own <Label> above the control — nothing is
   * emitted in that case. The c22a admin pages pass a real label.
   */
  label?: string;
  value: string;
  /** When `value` is set but `options` does not yet contain it (e.g. async subdivisions),
   *  this fallback label is shown in the closed trigger so the user still sees what
   *  they previously picked. */
  displayFallback?: string;
  onChange: (value: string) => void;
  options: SearchableOption[];
  /** Disabled state — also shows a spinner. */
  loading?: boolean;
  /** Placeholder text shown when value is empty and menu is closed. */
  placeholder?: string;
  /** When true, don't auto-focus the search input on menu open. */
  required?: boolean;
  /**
   * v0.4.9.2.4c2: colour scheme.
   *   'legacy' (default) — hardcoded slate/green, matches the c22a admin pages.
   *   'theme'             — shadcn design tokens, matches NetworkView and the
   *                         other theme-based pages.
   * Existing callers are unaffected: the default preserves today's markup.
   */
  theme?: 'legacy' | 'theme';
}

export function SearchableSelect({
  label = '',
  value,
  displayFallback,
  onChange,
  options,
  loading = false,
  placeholder = 'Search…',
  required = false,
  theme = 'legacy',
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const selected = options.find(o => o.value === value);
  const triggerLabel = selected?.label ?? displayFallback ?? '';

  const filtered = query.trim() === ''
    ? options
    : options.filter(o => {
        const q = query.trim().toLowerCase();
        return (
          o.label.toLowerCase().includes(q) ||
          (o.hint ?? '').toLowerCase().includes(q) ||
          o.value.toLowerCase().includes(q)
        );
      });

  useEffect(() => {
    if (open) {
      setQuery('');
      setHighlight(0);
      // Auto-focus the search input
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Click outside → close
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  function pick(opt: SearchableOption) {
    onChange(opt.value);
    setOpen(false);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(filtered.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[highlight]) pick(filtered[highlight]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    } else if (e.key === 'Backspace' && query === '' && value) {
      // Clear selection when user hits Backspace on empty query (only if no query).
      e.preventDefault();
      onChange('');
    }
  }

  return (
    <div className="relative">
      {/* v0.4.9.2.4c2: `label` is now optional. When the host page
          already renders its own <Label> (e.g. NetworkView's shadcn
          forms), pass label="" and nothing is emitted here. The c22a
          admin pages all pass a real label and are unaffected. */}
      {label ? (
        <label className="text-xs text-slate-500 uppercase tracking-wide">
          {label}{required ? ' *' : ''}
        </label>
      ) : null}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`${label ? 'mt-1' : ''} w-full px-3 py-2 border rounded text-sm text-left flex items-center justify-between focus:outline-none ${
          theme === 'theme'
            ? 'border-input bg-background focus:border-primary'
            : 'border-slate-300 bg-white focus:border-green-500'
        }`}
      >
        <span className={`truncate ${
          theme === 'theme'
            ? (triggerLabel ? 'text-foreground' : 'text-muted-foreground')
            : (triggerLabel ? 'text-slate-800' : 'text-slate-400')
        }`}>
          {triggerLabel || placeholder}
        </span>
        {loading ? (
          <Loader2 className={`w-4 h-4 animate-spin ${
            theme === 'theme' ? 'text-muted-foreground' : 'text-slate-400'
          }`} />
        ) : value ? (
          <X
            className={`w-4 h-4 ${
              theme === 'theme'
                ? 'text-muted-foreground hover:text-foreground'
                : 'text-slate-400 hover:text-slate-600'
            }`}
            onClick={(e) => { e.stopPropagation(); onChange(''); }}
          />
        ) : (
          <ChevronDown className={`w-4 h-4 ${
            theme === 'theme' ? 'text-muted-foreground' : 'text-slate-400'
          }`} />
        )}
      </button>
      {open && (
        <div
          ref={menuRef}
          className={`absolute z-30 mt-1 w-full border rounded shadow-lg max-h-72 overflow-y-auto ${
            theme === 'theme'
              ? 'bg-popover border-border'
              : 'bg-white border-slate-300'
          }`}
        >
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => { setQuery(e.target.value); setHighlight(0); }}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            className={`w-full px-3 py-2 border-b text-sm focus:outline-none ${
              theme === 'theme'
                ? 'border-border bg-background text-foreground'
                : 'border-slate-200'
            }`}
          />
          {filtered.length === 0 ? (
            <div className={`px-3 py-3 text-sm text-center ${
              theme === 'theme' ? 'text-muted-foreground' : 'text-slate-400'
            }`}>
              {loading ? 'Loading…' : 'No results'}
            </div>
          ) : (
            filtered.map((opt, i) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => pick(opt)}
                onMouseEnter={() => setHighlight(i)}
                className={`block w-full px-3 py-2 text-left text-sm ${
                  theme === 'theme'
                    ? (i === highlight ? 'bg-accent' : 'bg-background hover:bg-accent/50')
                    : (i === highlight ? 'bg-green-50' : 'bg-white hover:bg-slate-50')
                } ${opt.value === value ? (theme === 'theme' ? 'font-semibold text-primary' : 'font-semibold text-green-700')
                  : (theme === 'theme' ? 'text-foreground' : 'text-slate-800')}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate">{opt.label}</span>
                  {opt.hint && (
                    <span className={`text-xs ml-2 shrink-0 ${
                      theme === 'theme' ? 'text-muted-foreground' : 'text-slate-400'
                    }`}>{opt.hint}</span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Helper: convert a CountryEntry list to SearchableOption[] with the
 * "Spain (ESP)" / "BLR — Belarus" display pattern the user requested.
 */
export function countryListToOptions(
  countries: ReadonlyArray<{ code: string; alpha2: string; name: string }>
): SearchableOption[] {
  return countries.map(c => ({
    value: c.code,
    label: `${c.name} (${c.code})`,
    hint: c.alpha2,
  }));
}
